/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { AlertBanner } from '@opengeoweb/shared';
import { genericActions, genericSelectors } from '@opengeoweb/store';
import { WarningsTimeline } from './WarningsTimeline';
import { useWarnings } from '../../api/api-utils';
import type { WarningModuleStore } from '../../store/types';
import {
  getExpandedSectionsByPanelId,
  getWarningsFilterById,
} from '../../store/warningsFilters/selectors';
import { MAX_WARNINGS_DISPLAY } from '../../utils/constants';
import { useWarningsTranslation } from '../../utils/i18n';
import { createWarningFilterPredicate } from '../FilterList/filter-hooks';
import type { FilterState } from '../FilterList/filter-hooks';
import { TimeFilterMode } from '../FilterList/filter-utils';

export interface WarningsTimelineConnectProps {
  panelId: string;
}

interface WorkspaceViewsById {
  [viewId: string]: {
    componentType?: string;
  };
}

const getLinkedWarningListPanelId = (
  state: WarningModuleStore,
  panelId: string,
): string | undefined => {
  const viewsById =
    (
      state as {
        workspace?: {
          views?: { byId?: WorkspaceViewsById };
        };
      }
    ).workspace?.views?.byId || {};
  const linksByPanelId = state.syncGroups?.linkedState?.links || {};

  // Timeline is a read-only link target for the list (for now)
  return Object.keys(linksByPanelId).find((candidatePanelId) => {
    if (candidatePanelId === panelId) {
      return false;
    }
    const candidateType = viewsById[candidatePanelId]?.componentType;
    if (candidateType !== 'WarningList') {
      return false;
    }
    const candidateTargets = linksByPanelId[candidatePanelId] || [];
    return candidateTargets.includes(panelId);
  });
};

export const WarningsTimelineConnect: React.FC<
  WarningsTimelineConnectProps
> = ({ panelId }) => {
  const { t } = useWarningsTranslation();
  const { isLoggedIn } = useAuthenticationContext();
  const dispatch = useDispatch();

  const sourcePanelId = useSelector((state: WarningModuleStore) =>
    getLinkedWarningListPanelId(state, panelId),
  );

  const savedFilterState = useSelector((state: WarningModuleStore) =>
    getWarningsFilterById(state, sourcePanelId),
  );

  const expandedSections = useSelector((state: WarningModuleStore) =>
    getExpandedSectionsByPanelId(state, sourcePanelId),
  );

  const hoveredWarningId = useSelector((state: WarningModuleStore) =>
    sourcePanelId
      ? genericSelectors.selectSharedData(state, sourcePanelId).hoverId
      : undefined,
  );

  const { combinedWarnings } = useWarnings(
    !isLoggedIn || !sourcePanelId,
    savedFilterState.time,
  );

  const byFilterState = React.useMemo(
    () =>
      createWarningFilterPredicate(
        combinedWarnings,
        savedFilterState as FilterState,
      ),
    [combinedWarnings, savedFilterState],
  );

  const filteredWarnings = React.useMemo(
    () => combinedWarnings.filter(byFilterState),
    [byFilterState, combinedWarnings],
  );

  const displayedWarnings = React.useMemo(() => {
    const isOverLimit = filteredWarnings.length > MAX_WARNINGS_DISPLAY;
    const hasCustomTimeFilter =
      savedFilterState.time.mode === TimeFilterMode.custom;
    return hasCustomTimeFilter && isOverLimit ? [] : filteredWarnings;
  }, [filteredWarnings, savedFilterState.time.mode]);

  const warnings = React.useMemo(
    () =>
      displayedWarnings.filter(
        (warning) =>
          (warning.status && expandedSections[warning.status]) ??
          (warning.productStatus && expandedSections[warning.productStatus]),
      ),
    [displayedWarnings, expandedSections],
  );

  const handleHoverWarning = React.useCallback(
    (warningId?: string): void => {
      if (!sourcePanelId) {
        return;
      }

      dispatch(
        genericActions.addSharedData({
          panelId: sourcePanelId,
          data: { hoverId: warningId },
        }),
      );
    },
    [dispatch, sourcePanelId],
  );

  if (!isLoggedIn) {
    return (
      <AlertBanner
        dataTestId="warnings-timeline-auth-error"
        severity="error"
        title={t('error-auth-login')}
      />
    );
  }

  return (
    <WarningsTimeline
      hoveredWarningId={hoveredWarningId}
      onHoverWarning={handleHoverWarning}
      warnings={warnings}
    />
  );
};
